import type { inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import { useIntl } from "react-intl";

import type { AppRouter } from "@runcheetah/api/router";
import {
  WORKSPACE_BILLING_CHANGEABLE_STATUSES,
  WORKSPACE_BILLING_RECHECKOUT_STATUSES,
} from "@runcheetah/shared";
import { Button, cn } from "@runcheetah/ui";

import { BillingCard } from "./billing-card";
import { BillingEmptyState } from "./billing-empty-state";
import { BillingModalShell } from "./billing-modal-shell";
import { BillingSectionHeader } from "./billing-section-header";
import { BillingUsageMeter } from "./billing-usage-meter";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BillingOverview = RouterOutputs["brand"]["getBillingOverview"];
type BillingPlan = RouterOutputs["billing"]["listPlans"][number];
type CreditPack = RouterOutputs["billing"]["listCreditPacks"][number];
type BillingInvoice = RouterOutputs["billing"]["listInvoices"][number];
type BillingCycle = "monthly" | "yearly";

const formatDate = (value: string | null, locale: string): string | null => {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
};

const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale).format(value);

const formatMoney = (value: number | null, locale: string): string | null => {
  if (value === null) {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(value / 100);
};

const formatCurrency = (value: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value / 100);

const getStatusLabel = (status: NonNullable<BillingOverview["account"]>["status"]): string =>
  status.replaceAll("_", " ");

const formatInvoiceStatus = (status: string): string =>
  status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getPlanPrice = (plan: BillingPlan, billingCycle: BillingCycle): number | null =>
  billingCycle === "monthly" ? plan.monthlyPriceCents : plan.yearlyPriceCents;

const sortPlansByCyclePrice = (plans: BillingPlan[], billingCycle: BillingCycle) =>
  plans.toSorted((left, right) => {
    const leftPrice = getPlanPrice(left, billingCycle);
    const rightPrice = getPlanPrice(right, billingCycle);

    if (leftPrice === null && rightPrice !== null) {
      return 1;
    }
    if (leftPrice !== null && rightPrice === null) {
      return -1;
    }
    if (leftPrice !== null && rightPrice !== null && leftPrice !== rightPrice) {
      return leftPrice - rightPrice;
    }

    return (
      (left.monthlyCreditAllowance ?? 0) - (right.monthlyCreditAllowance ?? 0) ||
      (left.brandLimit ?? 0) - (right.brandLimit ?? 0) ||
      left.name.localeCompare(right.name)
    );
  });

export function BillingSection({
  overview,
  plans,
  creditPacks,
  invoices,
  isCheckoutPending,
  isCreditPackCheckoutPending,
  isPortalPending,
  isCancelPending,
  isResumePending,
  isInvoicesPending,
  billingActionError,
  onCheckout,
  onChangePlan,
  onCreditPackCheckout,
  onCancelSubscription,
  onResumeSubscription,
  onManageBilling,
}: {
  overview: BillingOverview;
  plans: BillingPlan[];
  creditPacks: CreditPack[];
  invoices: BillingInvoice[];
  isCheckoutPending: boolean;
  isCreditPackCheckoutPending: boolean;
  isPortalPending: boolean;
  isCancelPending: boolean;
  isResumePending: boolean;
  isInvoicesPending: boolean;
  billingActionError: string | null;
  onCheckout: (input: { planId: string; billingCycle: BillingCycle }) => void;
  onChangePlan: (input: { planId: string; billingCycle: BillingCycle }) => void;
  onCreditPackCheckout: (creditPackId: string) => void;
  onCancelSubscription: () => void;
  onResumeSubscription: () => void;
  onManageBilling: () => void;
}) {
  const intl = useIntl();
  const plan = overview.plan;
  const account = overview.account;
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>(
    account?.billingCycle ?? "monthly",
  );
  const canShowPeriodEnd = Boolean(
    account &&
    account.hasStripeSubscription &&
    WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status),
  );
  const periodEnd = formatDate(
    canShowPeriodEnd ? (account?.currentPeriodEnd ?? null) : null,
    intl.locale,
  );
  const pendingEffectiveAt = formatDate(account?.pendingEffectiveAt ?? null, intl.locale);
  const pendingPlan = plans.find((item) => item.id === account?.pendingPlanId) ?? null;
  const brandLimit = overview.usage.brands.total;
  const creditUsage = overview.usage.credits;
  const hasActiveSubscription = Boolean(
    plan &&
    account &&
    account.hasStripeSubscription &&
    !WORKSPACE_BILLING_RECHECKOUT_STATUSES.includes(account.status),
  );
  const hasPlanEntitlement = Boolean(
    plan &&
    account &&
    (WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status) ||
      account.isFailedPaymentGraceActive),
  );
  const displayedPlan = hasActiveSubscription ? plan : null;
  const displayedCreditTotal = displayedPlan || account ? creditUsage.total : 0;
  const displayedBrandLimit = hasPlanEntitlement ? brandLimit : 1;
  const displayedRunsLabel =
    account?.billingCycle === "yearly"
      ? intl.formatMessage({
          id: "brandSettings.billing.creditsUsageYearly",
          defaultMessage: "Runs this year",
        })
      : intl.formatMessage({
          id: "brandSettings.billing.creditsUsage",
          defaultMessage: "Runs this month",
        });
  const runsUnitLabel = intl.formatMessage({
    id: "brandSettings.billing.runsUnit",
    defaultMessage: "runs",
  });
  const hasPlan = hasActiveSubscription;
  const topUps = creditUsage.topUps ?? { remaining: 0, nextExpiresAt: null, packs: [] };
  const shouldShowCreditPackSection = topUps.packs.length > 0 || creditPacks.length > 0;
  const displayedSubscriptionRuns =
    displayedCreditTotal === null ? null : Math.max(0, displayedCreditTotal - topUps.remaining);
  const extraRunsDetail =
    topUps.remaining > 0
      ? intl.formatMessage(
          {
            id: "brandSettings.billing.extraRunsIncluded",
            defaultMessage: "+ {count} extra runs",
          },
          { count: formatNumber(topUps.remaining, intl.locale) },
        )
      : null;
  const canBuyCreditPacks = Boolean(
    account?.hasStripeSubscription &&
    WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status),
  );
  const canChangePlan = Boolean(
    account &&
    account.hasStripeSubscription &&
    WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status),
  );
  const canStartCheckout = Boolean(
    !account ||
    !account.hasStripeSubscription ||
    WORKSPACE_BILLING_RECHECKOUT_STATUSES.includes(account.status),
  );
  const shouldManageBillingBeforeChange = Boolean(account && !canChangePlan && !canStartCheckout);
  const hasPlans = plans.length > 0;
  const sortedPlans = sortPlansByCyclePrice(plans, selectedCycle);
  const openPlanModal = () => {
    if (shouldManageBillingBeforeChange) {
      onManageBilling();
      return;
    }

    setSelectedCycle(account?.billingCycle ?? "monthly");
    setIsPlanModalOpen(true);
  };
  const handlePlanAction = (availablePlan: BillingPlan) => {
    const input = { planId: availablePlan.id, billingCycle: selectedCycle };
    setIsPlanModalOpen(false);

    if (canChangePlan) {
      onChangePlan(input);
      return;
    }

    onCheckout(input);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {intl.formatMessage({ id: "brandSettings.billing.title", defaultMessage: "Billing" })}
        </h1>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          {intl.formatMessage({
            id: "brandSettings.billing.subtitle",
            defaultMessage:
              "Manage your subscription, payment method, billing details, and invoices.",
          })}
        </p>
      </div>

      <BillingCard>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {intl.formatMessage({
                  id: "brandSettings.billing.currentPlanLabel",
                  defaultMessage: "Current plan",
                })}
              </span>
              {account ? (
                <span className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-foreground">
                  {getStatusLabel(account.status)}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <div className="text-2xl font-semibold tracking-tight text-foreground">
                {displayedPlan?.name ??
                  intl.formatMessage({
                    id: "brandSettings.billing.freePlanTitle",
                    defaultMessage: "Free plan",
                  })}
              </div>
              {hasActiveSubscription && account ? (
                <div className="text-sm text-muted-foreground">
                  {intl.formatMessage(
                    {
                      id: "brandSettings.billing.billedCycle",
                      defaultMessage: "billed {cycle}",
                    },
                    { cycle: account.billingCycle },
                  )}
                </div>
              ) : null}
            </div>
            {periodEnd ? (
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                {intl.formatMessage(
                  account?.cancelAtPeriodEnd
                    ? {
                        id: "brandSettings.billing.currentPeriodCancelEnds",
                        defaultMessage: "Your subscription ends on {date}.",
                      }
                    : {
                        id: "brandSettings.billing.currentPeriodEnds",
                        defaultMessage: "Your subscription renews on {date}.",
                      },
                  { date: periodEnd },
                )}
              </p>
            ) : null}
            {pendingPlan && pendingEffectiveAt ? (
              <div className="rounded-2xl border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-950">
                {intl.formatMessage(
                  {
                    id: "brandSettings.billing.pendingPlanChange",
                    defaultMessage: "Scheduled change to {planName} ({cycle}) on {date}.",
                  },
                  {
                    planName: pendingPlan.name,
                    cycle: account?.pendingBillingCycle ?? "monthly",
                    date: pendingEffectiveAt,
                  },
                )}
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            className="shrink-0"
            disabled={
              (!hasPlans && !shouldManageBillingBeforeChange) ||
              isCheckoutPending ||
              isPortalPending
            }
            onClick={openPlanModal}
          >
            {isCheckoutPending || isPortalPending
              ? intl.formatMessage({
                  id: "brandSettings.billing.openingCheckout",
                  defaultMessage: "Opening checkout...",
                })
              : shouldManageBillingBeforeChange
                ? intl.formatMessage({
                    id: "brandSettings.billing.resolveBilling",
                    defaultMessage: "Resolve billing",
                  })
                : canChangePlan
                  ? intl.formatMessage({
                      id: "brandSettings.billing.changePlan",
                      defaultMessage: "Change plan",
                    })
                  : intl.formatMessage({
                      id: "brandSettings.billing.subscribe",
                      defaultMessage: "Subscribe",
                    })}
          </Button>
        </div>

        <div className="mt-6 grid gap-4 border-t border-border/70 pt-6 lg:grid-cols-2">
          {displayedCreditTotal == null ? (
            <BillingEmptyState
              text={intl.formatMessage({
                id: "brandSettings.billing.noCreditAllowance",
                defaultMessage: "No monthly run allowance is configured.",
              })}
            />
          ) : (
            <BillingUsageMeter
              label={displayedRunsLabel}
              used={creditUsage.used}
              total={displayedSubscriptionRuns ?? displayedCreditTotal}
              unit={runsUnitLabel}
              detail={extraRunsDetail}
              showPercentage={false}
            />
          )}
          {displayedBrandLimit === null ? (
            <div className="space-y-3 rounded-2xl border border-border/70 bg-muted/20 p-4">
              <div className="text-sm font-medium text-foreground">
                {intl.formatMessage({
                  id: "brandSettings.billing.brandsUsage",
                  defaultMessage: "Brands",
                })}
              </div>
              <div className="text-sm font-semibold text-foreground">
                {intl.formatMessage(
                  {
                    id: "brandSettings.billing.brandsUsedUnlimited",
                    defaultMessage: "{used} brands used",
                  },
                  { used: formatNumber(overview.usage.brands.used, intl.locale) },
                )}
              </div>
            </div>
          ) : (
            <BillingUsageMeter
              label={intl.formatMessage({
                id: "brandSettings.billing.brandsUsage",
                defaultMessage: "Brands",
              })}
              used={overview.usage.brands.used}
              total={displayedBrandLimit}
              unit={intl.formatMessage({
                id: "brandSettings.billing.brandsUnit",
                defaultMessage: "brands",
              })}
              showPercentage={false}
            />
          )}
        </div>
      </BillingCard>

      {billingActionError ? (
        <div className="rounded-2xl border border-red-800/20 bg-red-50 p-4 text-sm text-red-950">
          {billingActionError}
        </div>
      ) : null}

      {shouldShowCreditPackSection ? (
        <BillingCard className="overflow-hidden border-border/50 bg-card/95 shadow-sm">
          <div className="space-y-5">
            <BillingSectionHeader
              title={intl.formatMessage({
                id: "brandSettings.billing.creditPacksTitle",
                defaultMessage: "Extra runs",
              })}
              description={intl.formatMessage({
                id: "brandSettings.billing.creditPacksDescription",
                defaultMessage:
                  "For busy weeks when your plan allowance runs out. Plan runs are always used first; extra runs only cover the overflow.",
              })}
              action={
                <div className="rounded-full bg-primary/5 px-3 py-1.5 text-xs font-semibold text-foreground/65">
                  {intl.formatMessage({
                    id: "brandSettings.billing.extraRunsKicker",
                    defaultMessage: "Optional add-on",
                  })}
                </div>
              }
            />

            {topUps.packs.length ? (
              <div className="rounded-2xl bg-muted/15 p-4 text-foreground shadow-[inset_0_0_0_1px_rgba(19,34,49,.05)]">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({
                      id: "brandSettings.billing.activeTopUpsTitle",
                      defaultMessage: "Available extra runs",
                    })}
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground">
                    {intl.formatMessage(
                      {
                        id: "brandSettings.billing.topUpAvailableSummary",
                        defaultMessage: "{count} extra runs available",
                      },
                      { count: formatNumber(topUps.remaining, intl.locale) },
                    )}
                  </div>
                </div>
                <div className="mt-3 grid gap-2">
                  {topUps.packs.map((pack) => (
                    <div
                      key={pack.id}
                      className="flex flex-col gap-2 rounded-xl bg-background/80 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="font-medium text-foreground">
                          {intl.formatMessage(
                            {
                              id: "brandSettings.billing.runPackName",
                              defaultMessage: "{count} run pack",
                            },
                            { count: formatNumber(pack.creditsPurchased, intl.locale) },
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {intl.formatMessage(
                            {
                              id: "brandSettings.billing.topUpPackUsage",
                              defaultMessage: "{deducted} used · {remaining} left from {total}",
                            },
                            {
                              deducted: formatNumber(pack.creditsUsed, intl.locale),
                              remaining: formatNumber(pack.remaining, intl.locale),
                              total: formatNumber(pack.creditsPurchased, intl.locale),
                            },
                          )}
                        </div>
                      </div>
                      {pack.expiresAt ? (
                        <div className="shrink-0 text-xs font-medium text-muted-foreground">
                          {intl.formatMessage(
                            {
                              id: "brandSettings.billing.topUpPackExpires",
                              defaultMessage: "Expires {date}",
                            },
                            { date: formatDate(pack.expiresAt, intl.locale) },
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {creditPacks.length ? (
              <div className="space-y-2">
                {creditPacks.map((pack) => (
                  <div
                    key={pack.id}
                    className="flex flex-col gap-3 rounded-2xl bg-background/70 px-4 py-3 shadow-[inset_0_0_0_1px_rgba(19,34,49,.055)] transition hover:bg-background sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        {intl.formatMessage(
                          {
                            id: "brandSettings.billing.runPackName",
                            defaultMessage: "{count} run pack",
                          },
                          { count: formatNumber(pack.credits, intl.locale) },
                        )}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold tracking-tight text-foreground">
                          {formatNumber(pack.credits, intl.locale)}
                        </span>
                        <span className="text-sm font-medium text-muted-foreground">
                          {intl.formatMessage({
                            id: "brandSettings.billing.runsUnit",
                            defaultMessage: "runs",
                          })}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {intl.formatMessage(
                          {
                            id: "brandSettings.billing.creditPackExpiry",
                            defaultMessage: "Valid for {days} days",
                          },
                          { days: pack.expiresAfterDays },
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
                      <div className="text-sm font-semibold text-foreground">
                        {formatMoney(pack.priceCents, intl.locale)}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!canBuyCreditPacks || isCreditPackCheckoutPending}
                        onClick={() => onCreditPackCheckout(pack.id)}
                      >
                        {isCreditPackCheckoutPending
                          ? intl.formatMessage({
                              id: "brandSettings.billing.openingCheckout",
                              defaultMessage: "Opening checkout...",
                            })
                          : intl.formatMessage({
                              id: "brandSettings.billing.buyCredits",
                              defaultMessage: "Buy extra runs",
                            })}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : topUps.packs.length ? null : (
              <BillingEmptyState
                text={intl.formatMessage({
                  id: "brandSettings.billing.noCreditPacks",
                  defaultMessage: "Run packs are not available yet.",
                })}
              />
            )}
          </div>
        </BillingCard>
      ) : null}

      <BillingCard>
        <div className="space-y-5">
          <BillingSectionHeader
            title={intl.formatMessage({
              id: "brandSettings.billing.invoicesTitle",
              defaultMessage: "Invoices",
            })}
            description={intl.formatMessage({
              id: "brandSettings.billing.invoicesDescription",
              defaultMessage: "Your billing history. Download any invoice as a PDF.",
            })}
          />

          {isInvoicesPending ? (
            <BillingEmptyState
              text={intl.formatMessage({
                id: "brandSettings.billing.invoicesLoading",
                defaultMessage: "Loading invoices...",
              })}
            />
          ) : invoices.length ? (
            <div className="overflow-hidden rounded-2xl border border-border/70">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border/70 text-sm">
                  <thead className="bg-muted/30 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left">
                        {intl.formatMessage({
                          id: "brandSettings.billing.tableDate",
                          defaultMessage: "Date",
                        })}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left">
                        {intl.formatMessage({
                          id: "brandSettings.billing.tableDescription",
                          defaultMessage: "Description",
                        })}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left">
                        {intl.formatMessage({
                          id: "brandSettings.billing.tableAmount",
                          defaultMessage: "Amount",
                        })}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left">
                        {intl.formatMessage({
                          id: "brandSettings.billing.tableStatus",
                          defaultMessage: "Status",
                        })}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right">
                        {intl.formatMessage({
                          id: "brandSettings.billing.tableInvoice",
                          defaultMessage: "Invoice",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/70 bg-card">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                          {formatDate(invoice.date, intl.locale)}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">
                          {invoice.description}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-foreground">
                          {formatCurrency(invoice.amountCents, invoice.currency, intl.locale)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                          {formatInvoiceStatus(invoice.status)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          {invoice.pdfUrl ? (
                            <Button asChild size="sm" variant="outline">
                              <a href={invoice.pdfUrl} target="_blank" rel="noreferrer">
                                {intl.formatMessage({
                                  id: "brandSettings.billing.invoicePdf",
                                  defaultMessage: "PDF",
                                })}
                              </a>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <BillingEmptyState
              text={intl.formatMessage({
                id: "brandSettings.billing.noInvoices",
                defaultMessage: "No invoices yet.",
              })}
            />
          )}
        </div>
      </BillingCard>

      {account?.hasStripeCustomer ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-muted/15 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium text-foreground">
              {intl.formatMessage({
                id: "brandSettings.billing.billingDetailsTitle",
                defaultMessage: "Billing details",
              })}
            </div>
            <div className="text-xs leading-5 text-muted-foreground">
              {intl.formatMessage({
                id: "brandSettings.billing.billingDetailsDescription",
                defaultMessage: "Manage payment methods and billing details in Stripe.",
              })}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isPortalPending}
            onClick={onManageBilling}
          >
            {isPortalPending
              ? intl.formatMessage({
                  id: "brandSettings.billing.openingPortal",
                  defaultMessage: "Opening...",
                })
              : intl.formatMessage({
                  id: "brandSettings.billing.manageBilling",
                  defaultMessage: "Manage billing",
                })}
          </Button>
        </div>
      ) : null}

      {hasPlan ? (
        <div className="mt-7 flex flex-col gap-4 border-t border-border/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-bold text-foreground">
              {account?.cancelAtPeriodEnd
                ? intl.formatMessage({
                    id: "brandSettings.billing.cancelScheduledTitle",
                    defaultMessage: "Subscription scheduled to cancel",
                  })
                : intl.formatMessage({
                    id: "brandSettings.billing.cancelSubscriptionTitle",
                    defaultMessage: "Cancel subscription",
                  })}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {account?.cancelAtPeriodEnd && periodEnd
                ? intl.formatMessage(
                    {
                      id: "brandSettings.billing.cancelScheduledDescription",
                      defaultMessage:
                        "Your subscription is scheduled to end on {date}. You'll keep access until then.",
                    },
                    { date: periodEnd },
                  )
                : intl.formatMessage({
                    id: "brandSettings.billing.cancelSubscriptionDescription",
                    defaultMessage: "You'll keep access until the end of your billing period.",
                  })}
            </div>
          </div>
          <button
            type="button"
            data-testid="button-cancel-subscription"
            disabled={isCancelPending || isResumePending}
            onClick={() =>
              account?.cancelAtPeriodEnd ? setIsResumeModalOpen(true) : setIsCancelModalOpen(true)
            }
            className={cn(
              "rounded-[10px] border bg-white px-3.5 py-2 text-[13px] font-bold transition disabled:cursor-not-allowed disabled:opacity-60",
              account?.cancelAtPeriodEnd
                ? "border-primary/25 text-primary hover:bg-primary/5"
                : "border-red-700/25 text-red-700 hover:bg-red-700/5",
            )}
          >
            {isCancelPending || isResumePending
              ? account?.cancelAtPeriodEnd
                ? intl.formatMessage({
                    id: "brandSettings.billing.resumingSubscription",
                    defaultMessage: "Resuming...",
                  })
                : intl.formatMessage({
                    id: "brandSettings.billing.cancelingSubscription",
                    defaultMessage: "Canceling...",
                  })
              : account?.cancelAtPeriodEnd
                ? intl.formatMessage({
                    id: "brandSettings.billing.keepSubscriptionButton",
                    defaultMessage: "Resume subscription",
                  })
                : intl.formatMessage({
                    id: "brandSettings.billing.cancelSubscriptionTitle",
                    defaultMessage: "Cancel subscription",
                  })}
          </button>
        </div>
      ) : null}

      {isCancelModalOpen && plan ? (
        <BillingModalShell
          title={intl.formatMessage({
            id: "brandSettings.billing.cancelModalTitle",
            defaultMessage: "Cancel subscription?",
          })}
          description={intl.formatMessage(
            {
              id: "brandSettings.billing.cancelModalDescription",
              defaultMessage:
                "Your {planName} plan will remain active until the end of the current billing period.",
            },
            { planName: plan.name },
          )}
          onClose={() => setIsCancelModalOpen(false)}
          widthClassName="max-w-lg"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setIsCancelModalOpen(false)}>
                {intl.formatMessage({
                  id: "brandSettings.billing.cancelModalKeep",
                  defaultMessage: "Keep subscription",
                })}
              </Button>
              <Button
                type="button"
                disabled={isCancelPending}
                className="bg-red-700 text-white hover:bg-red-800"
                onClick={() => {
                  onCancelSubscription();
                  setIsCancelModalOpen(false);
                }}
              >
                {isCancelPending
                  ? intl.formatMessage({
                      id: "brandSettings.billing.cancelingSubscription",
                      defaultMessage: "Canceling...",
                    })
                  : intl.formatMessage({
                      id: "brandSettings.billing.cancelModalConfirm",
                      defaultMessage: "Cancel subscription",
                    })}
              </Button>
            </>
          }
        >
          <div className="rounded-2xl border border-red-700/15 bg-red-50 p-4 text-sm leading-6 text-red-950">
            {intl.formatMessage(
              {
                id: "brandSettings.billing.cancelModalBody",
                defaultMessage:
                  "You'll keep full access to {planName} until the end of your current billing period. After that, your subscription will end.",
              },
              { planName: plan.name },
            )}
          </div>
        </BillingModalShell>
      ) : null}

      {isResumeModalOpen && plan ? (
        <BillingModalShell
          title={intl.formatMessage({
            id: "brandSettings.billing.resumeModalTitle",
            defaultMessage: "Resume subscription",
          })}
          description={intl.formatMessage(
            {
              id: "brandSettings.billing.resumeModalDescription",
              defaultMessage:
                "Your {planName} subscription will stay active and future subscription charges will continue.",
            },
            { planName: plan.name },
          )}
          onClose={() => setIsResumeModalOpen(false)}
          widthClassName="max-w-lg"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setIsResumeModalOpen(false)}>
                {intl.formatMessage({
                  id: "brandSettings.billing.resumeModalClose",
                  defaultMessage: "Cancel",
                })}
              </Button>
              <Button
                type="button"
                disabled={isResumePending}
                onClick={() => {
                  onResumeSubscription();
                  setIsResumeModalOpen(false);
                }}
              >
                {isResumePending
                  ? intl.formatMessage({
                      id: "brandSettings.billing.resumingSubscription",
                      defaultMessage: "Resuming...",
                    })
                  : intl.formatMessage({
                      id: "brandSettings.billing.resumeModalConfirm",
                      defaultMessage: "Resume subscription",
                    })}
              </Button>
            </>
          }
        >
          <></>
        </BillingModalShell>
      ) : null}

      {isPlanModalOpen ? (
        <BillingModalShell
          title={intl.formatMessage({
            id: hasPlan
              ? "brandSettings.billing.planModalChangeTitle"
              : "brandSettings.billing.planModalUpgradeTitle",
            defaultMessage: hasPlan ? "Change your plan" : "Upgrade your plan",
          })}
          description={
            hasPlan && displayedPlan
              ? intl.formatMessage(
                  {
                    id: "brandSettings.billing.planModalChangeDescription",
                    defaultMessage:
                      "You're currently on {planName}. Pick a new plan — changes take effect on the next billing cycle.",
                  },
                  { planName: displayedPlan.name },
                )
              : intl.formatMessage({
                  id: "brandSettings.billing.planModalUpgradeDescription",
                  defaultMessage:
                    "Pick a plan to unlock more brands, runs, and automation. Changes take effect immediately.",
                })
          }
          onClose={() => setIsPlanModalOpen(false)}
          widthClassName="max-w-[680px]"
        >
          <div className="flex justify-center">
            <div className="inline-flex rounded-full border border-[rgba(13,31,45,.08)] bg-[rgba(13,31,45,.035)] p-1">
              {(["monthly", "yearly"] satisfies BillingCycle[]).map((cycle) => (
                <Button
                  key={cycle}
                  type="button"
                  size="sm"
                  variant={selectedCycle === cycle ? "default" : "ghost"}
                  className={cn(
                    "rounded-full px-5 text-xs font-extrabold tracking-[-0.01em]",
                    selectedCycle === cycle
                      ? "bg-[#0D1F2D] text-[#C8FF00] hover:bg-[#0D1F2D]/95"
                      : "text-[#0D1F2D]/65 hover:bg-white/70 hover:text-[#0D1F2D]",
                  )}
                  onClick={() => setSelectedCycle(cycle)}
                >
                  {cycle === "monthly"
                    ? intl.formatMessage({
                        id: "brandSettings.billing.monthlyCycle",
                        defaultMessage: "Monthly",
                      })
                    : intl.formatMessage({
                        id: "brandSettings.billing.yearlyCycle",
                        defaultMessage: "Yearly",
                      })}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid gap-[14px] md:grid-cols-2">
            {plans.length ? (
              sortedPlans.map((availablePlan) => {
                const price = getPlanPrice(availablePlan, selectedCycle);
                const isCurrentPlan = Boolean(
                  hasActiveSubscription &&
                  plan?.id === availablePlan.id &&
                  account?.billingCycle === selectedCycle,
                );
                const canUseSelectedCycle = price !== null;
                const featured = availablePlan.isMostPopular;

                return (
                  <div
                    key={availablePlan.id}
                    className={cn(
                      "relative flex min-h-[360px] flex-col gap-[14px] rounded-[14px] border p-5",
                      featured
                        ? "border-[#0D1F2D] bg-[#0D1F2D] text-white"
                        : "border-[rgba(13,31,45,.08)] bg-white text-[#0D1F2D]",
                    )}
                  >
                    {featured ? (
                      <span className="absolute -top-[10px] right-4 rounded-full bg-[#C8FF00] px-[9px] py-[3px] text-[10px] font-extrabold uppercase tracking-[0.05em] text-[#0D1F2D]">
                        {intl.formatMessage({
                          id: "brandSettings.billing.mostPopular",
                          defaultMessage: "Most popular",
                        })}
                      </span>
                    ) : null}

                    <div>
                      <div className="text-sm font-extrabold tracking-[-0.01em]">
                        {availablePlan.name}
                      </div>
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[28px] font-extrabold tracking-[-0.02em]">
                          {formatMoney(price, intl.locale) ??
                            intl.formatMessage({
                              id: "brandSettings.billing.priceUnavailable",
                              defaultMessage: "N/A",
                            })}
                        </span>
                        {price !== null ? (
                          <span
                            className={cn(
                              "text-[11px]",
                              featured ? "text-white/65" : "text-[rgba(13,31,45,.6)]",
                            )}
                          >
                            {intl.formatMessage(
                              selectedCycle === "yearly"
                                ? {
                                    id: "brandSettings.billing.planPerYear",
                                    defaultMessage: "/yr",
                                  }
                                : {
                                    id: "brandSettings.billing.planPerMonth",
                                    defaultMessage: "/mo",
                                  },
                            )}
                          </span>
                        ) : null}
                      </div>
                      <div
                        className={cn(
                          "text-[11px]",
                          featured ? "text-white/55" : "text-[rgba(13,31,45,.6)]",
                        )}
                      >
                        {selectedCycle === "yearly"
                          ? intl.formatMessage({
                              id: "brandSettings.billing.planBilledYearly",
                              defaultMessage: "Billed yearly",
                            })
                          : intl.formatMessage({
                              id: "brandSettings.billing.planBilledMonthly",
                              defaultMessage: "Billed monthly",
                            })}
                      </div>
                    </div>

                    <ul className="m-0 flex list-none flex-col gap-1.5 border-t border-current/10 pt-3 text-xs leading-[1.4]">
                      <li className="flex items-start gap-[7px]">
                        <span
                          aria-hidden="true"
                          className={cn(
                            "mt-px font-extrabold",
                            featured ? "text-[#C8FF00]" : "text-[#7A9900]",
                          )}
                        >
                          ✓
                        </span>
                        <span className={featured ? "text-white/92" : "text-[#0D1F2D]"}>
                          {intl.formatMessage(
                            selectedCycle === "yearly"
                              ? {
                                  id: "brandSettings.billing.planAllowanceSummaryYearly",
                                  defaultMessage: "{brandLimit} brands · {credits} runs/year",
                                }
                              : {
                                  id: "brandSettings.billing.planAllowanceSummary",
                                  defaultMessage: "{brandLimit} brands · {credits} runs/month",
                                },
                            {
                              brandLimit: availablePlan.brandLimit ?? 0,
                              credits:
                                selectedCycle === "yearly"
                                  ? (availablePlan.monthlyCreditAllowance ?? 0) * 12
                                  : (availablePlan.monthlyCreditAllowance ?? 0),
                            },
                          )}
                        </span>
                      </li>
                    </ul>

                    <Button
                      type="button"
                      disabled={!canUseSelectedCycle || isCurrentPlan || isCheckoutPending}
                      onClick={() => handlePlanAction(availablePlan)}
                      className={cn(
                        "mt-auto rounded-[10px] px-[14px] py-2.5 text-[13px] font-extrabold tracking-[-0.01em]",
                        featured
                          ? "bg-[#C8FF00] text-[#0D1F2D] hover:bg-[#C8FF00]/90"
                          : "bg-[#0D1F2D] text-[#C8FF00] hover:bg-[#0D1F2D]/95",
                      )}
                    >
                      {isCurrentPlan
                        ? intl.formatMessage({
                            id: "brandSettings.billing.planCurrent",
                            defaultMessage: "Current plan",
                          })
                        : isCheckoutPending
                          ? intl.formatMessage({
                              id: "brandSettings.billing.openingCheckout",
                              defaultMessage: "Opening checkout...",
                            })
                          : canChangePlan
                            ? intl.formatMessage({
                                id: "brandSettings.billing.changePlan",
                                defaultMessage: "Change plan",
                              })
                            : intl.formatMessage({
                                id: "brandSettings.billing.subscribe",
                                defaultMessage: "Subscribe",
                              })}
                    </Button>
                  </div>
                );
              })
            ) : (
              <BillingEmptyState
                text={intl.formatMessage({
                  id: "brandSettings.billing.noStripePlans",
                  defaultMessage: "No Stripe-backed plans are available yet.",
                })}
              />
            )}
          </div>
        </BillingModalShell>
      ) : null}
    </div>
  );
}
